/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import type { ActionPillProps } from "./ActionPillProps";
import type { ContentDetailAction } from "../../ContentDetailScreen/ContentDetail/ContentDetailProps";
import "./ActionPill.scss";

/**
 * Resolves whether the action at the given index should render focused.
 * When `focusedActionIndex` is provided it is authoritative; otherwise the
 * action's own `isFocused` flag is used.
 *
 * Shared by {@link WatchActionsRow} and {@link ScheduleActionsRow} so the
 * focus-resolution logic lives in one place instead of being duplicated per
 * row.
 *
 * @param action - The action to resolve focus for
 * @param index - The action's position within the row
 * @param focusedActionIndex - Index of the currently D-pad-focused action
 * @returns True when the action should render its focused state
 */
export const resolveActionPillFocus = (
  action: ContentDetailAction,
  index: number,
  focusedActionIndex?: number
): boolean => {
  if (focusedActionIndex !== undefined) {
    return index === focusedActionIndex;
  }
  return action.isFocused === true;
};

/**
 * ActionPill — a single action button ("Watch On Now", "Record Series",
 * "Bookmark", …). Purely presentational — behaviour comes from the injected
 * `onSelect`.
 *
 * Shared by {@link WatchActionsRow}, {@link ScheduleActionsRow} and the
 * actions sheet on the Content Detail screen, each of which picks the `shape`
 * its row is drawn with.
 */
const ActionPill: FunctionComponent<ActionPillProps> = (
  props: ActionPillProps
): JSX.Element => {
  const { action, shape = "pill" } = props;

  // Shape comes from the row, focus from the caller — the two are independent,
  // which is the whole point: selecting a pill must not reshape it.
  const className: string =
    "cd-action cd-action--" +
    shape +
    (action.isFocused === true ? " cd-action--focused" : "") +
    (action.isDisabled === true ? " cd-action--disabled" : "");

  return (
    <button
      className={className}
      type="button"
      tabIndex={0}
      disabled={action.isDisabled === true}
      onClick={action.onSelect}
      onMouseEnter={action.onHover}
    >
      {action.icon !== undefined && (
        <span className="cd-action__icon">{action.icon}</span>
      )}
      <span className="cd-action__label">{action.label}</span>
    </button>
  );
};

export default ActionPill;
